// Typed client for the FNMS API (/api/v1). Cookies carry the session; every call sends the CSRF header.

import type { BudgetResult } from '../../src/domain/optics'
import type { AssetStatus, CableTier, Connection, EndpointRef, LngLat, NodeKind, Olt, Onu, PlantNode, Service, SlackLoop } from '../../src/domain/types'
import type { PathStep } from '../../src/domain/describe'
import type { CoreIdentity } from '../../src/domain/fiberColors'

export type { BudgetResult, EndpointRef, LngLat, NodeKind, PathStep, CableTier, CoreIdentity, Onu }

export type Role = 'viewer' | 'noc' | 'engineer' | 'admin'
export type Permission = 'read' | 'incident.ack' | 'demo.alarm' | 'audit.read' | 'plant.edit'
export interface User {
  username: string
  displayName: string
  role: Role
  permissions: Permission[]
}
export const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', noc: 'NOC', engineer: 'OSP engineer', admin: 'Admin (IT)' }

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly issues?: string[]) {
    super(message)
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: { 'X-FNMS-Request': '1', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new ApiError(0, 'Cannot reach the FNMS server. Check your connection and try again.')
  }
  if (res.status === 204) return undefined as T
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.issues)
  return data as T
}

export interface NetworkCable {
  id: string
  name: string
  tier: CableTier
  status: AssetStatus
  fromNodeId: string
  toNodeId: string
  fiberCount: number
  usedCores: number
  routeLengthM: number
  fiberLengthM: number
  path: LngLat[]
}

export interface NetworkResponse {
  summary: {
    counts: {
      nodes: number
      cables: number
      splices: number
      splitters: number
      services: number
      traceable: number
      deviating: number
      lowMargin: number
      napPortsTotal: number
      napPortsUsed: number
    }
    kmByTier: Partial<Record<CableTier, number>>
  }
  nodes: PlantNode[]
  cables: NetworkCable[]
  olts: Olt[]
}

export interface EndpointView {
  ref: EndpointRef
  key: string
  label: string
  connectionId: string | null
  connectedTo: string | null
  servesSubscriber: boolean
}

export type PortView =
  | { port: number; state: 'subscriber'; service: { id: string; label: string; accountRef: string; onu: Onu | null }; budget: BudgetResult | null }
  | { port: number; state: 'spliced'; to: string }
  | { port: number; state: 'free' }

export interface SplitterView {
  id: string
  name: string
  nodeId: string
  ratio: number
  insertionLossDb?: number
  inputConnected: boolean
  inputFrom: string | null
  inputEndpoint: EndpointView
  ports: PortView[]
}

export interface NodeDetail {
  node: PlantNode
  cables: {
    id: string
    name: string
    tier: CableTier
    fiberCount: number
    end: 'A' | 'B'
    otherNodeId: string
    otherNodeName?: string
    cores: { core: number; identity: CoreIdentity; endpoint: EndpointView }[]
  }[]
  olts: (Olt & { ports: EndpointView[] })[]
  splitters: SplitterView[]
  connections: { id: string; kind: Connection['kind']; tray: number | null; lossDb: number | null; a: string; b: string; createdBy: string; createdAt: string }[]
}

export interface CableDetail {
  cable: NetworkCable & { fibersPerTube: number; overlengthFactor: number; measuredLengthM?: number; fromNodeName: string; toNodeName: string }
  cores: { core: number; identity: CoreIdentity; a: EndpointView; b: EndpointView }[]
  slackLoops: (SlackLoop & { nodeName: string | null })[]
}

export interface TraceResponse {
  service: Service
  napNodeId: string
  steps: PathStep[]
  end: { kind: 'pon' | 'open' | 'loop' | 'service'; nodeId?: string }
  budget: BudgetResult | null
  opticClass: 'B+' | 'C+' | null
  cableIds: string[]
}

export type ImpactTarget = { kind: 'cable'; cableId: string } | { kind: 'node'; nodeId: string } | { kind: 'pon'; oltId: string; port: number }

export interface ImpactResponse {
  target: ImpactTarget
  affectedServiceIds: string[]
  byNap: { nodeId: string; name: string; count: number }[]
  byPon: { oltId: string; port: number; count: number; label: string }[]
  untraceableServiceIds: string[]
  services: { id: string; label: string; accountRef: string; napNodeId: string }[]
}

export type OtdrResponse = { fromLabel: string; walkedCableIds: string[] } & (
  | {
      status: 'located'
      cableId: string
      cableName: string
      core: number
      fiberIntoCableM: number
      routeFromEntryM: number
      routeToExitM: number
      entryNodeId: string
      entryNodeName: string
      exitNodeId: string
      exitNodeName: string
      point: LngLat
      uncertaintyM: number
      slackNodeName: string | null
    }
  | { status: 'at-node'; nodeId: string; nodeName: string; point: LngLat; uncertaintyM: number }
  | { status: 'beyond-splitter'; nodeId: string; nodeName: string; splitterName: string; remainingM: number }
  | { status: 'beyond-end'; endNodeId: string; endNodeName: string; overshootM: number }
)

export interface QualityIssue {
  severity: 'high' | 'medium' | 'low'
  kind: string
  title: string
  detail: string
  nodeId?: string
  serviceId?: string
}

export interface SearchResult {
  type: 'node' | 'cable' | 'service'
  id: string
  label: string
  sublabel: string
  nodeId?: string
}

export interface ImportPlan {
  sites: { ref: string; name: string; kind: string; location: LngLat; duplicateOfNodeId?: string; source: string }[]
  cables: { ref: string; name: string; tier: CableTier; fiberCount: number; lengthM: number; fromLabel: string; toLabel: string; source: string }[]
  issues: { severity: 'error' | 'warning'; message: string; source: string }[]
  summary: { sitesNew: number; sitesDuplicate: number; cablesNew: number; cablesUnjoined: number; errors: number; warnings: number }
  placemarks: number
  folders: string[]
}

export interface ImportOptionsIn {
  toleranceM: number
  status: 'active' | 'planned'
  splitterRatio: number
  locality?: string
}

export interface AuditEntry {
  id: string
  at: string
  actor: string
  action: string
  nodeId: string
  targetId: string
  summary: string
}

export interface SuspectView {
  kind: 'cable' | 'node' | 'splitter' | 'pon'
  id: string
  behind: number
  down: number
  confidence: number
  name: string
  what: string
  detail: string
  ends: { nodeId: string; name: string; location: LngLat }[]
  path: LngLat[] | null
}

export interface IncidentView {
  id: string
  kind: 'fibre' | 'power' | 'drop'
  title: string
  startedAt: string
  detectedAt: string
  serviceIds: string[]
  suspects: SuspectView[]
  byNap: { nodeId: string; name: string; count: number; location?: LngLat }[]
  byPon: { oltId: string; port: number; count: number; label: string; behind: number }[]
  ack: { by: string; at: string } | null
  otdrFrom: EndpointRef | null
  subscribers: { id: string; accountRef: string; label: string; nap: string; port: number }[]
  localities: string[]
}

export interface IncidentsResponse {
  source: string
  refreshedAt: string
  synthetic: boolean
  incidents: IncidentView[]
  resolved: { id: string; kind: IncidentView['kind']; title: string; subscribers: number; startedAt: string; resolvedAt: string }[]
  totals: { open: number; subscribersDown: number }
}

export type AddableKind = 'nap' | 'lcp' | 'closure' | 'pole' | 'handhole'

export interface AssetOptions {
  suggestedName: string
  suggestedCableName: string
  splitterRatios: number[]
  fiberCounts: number[]
  streetRouting: boolean
  persistence: string
}

export const api = {
  me: () => call<{ user: User; synthetic: boolean }>('GET', '/auth/me'),
  login: (username: string, password: string) => call<{ user: User; synthetic: boolean }>('POST', '/auth/login', { username, password }),
  logout: () => call<void>('POST', '/auth/logout'),
  network: () => call<NetworkResponse>('GET', '/network'),
  node: (id: string) => call<NodeDetail>('GET', `/nodes/${encodeURIComponent(id)}`),
  cable: (id: string) => call<CableDetail>('GET', `/cables/${encodeURIComponent(id)}`),
  trace: (serviceId: string) => call<TraceResponse>('GET', `/services/${encodeURIComponent(serviceId)}/trace`),
  impact: (target: ImpactTarget) => call<ImpactResponse>('POST', '/analysis/impact', target),
  otdr: (from: EndpointRef, distanceM: number) => call<OtdrResponse>('POST', '/analysis/otdr', { from, distanceM }),
  quality: () => call<{ issues: QualityIssue[] }>('GET', '/quality'),
  search: (q: string) => call<{ results: SearchResult[] }>('GET', `/search?q=${encodeURIComponent(q)}`),
  audit: () => call<{ entries: AuditEntry[] }>('GET', '/audit?limit=200'),
  incidents: () => call<IncidentsResponse>('GET', '/incidents'),
  portPath: (splitterId: string, port: number) =>
    call<{
      steps: PathStep[]
      reachesOlt: boolean
      end: { kind: string; nodeId?: string; nodeName?: string; at?: string }
      olt: string | null
      budget: BudgetResult | null
      cableIds: string[]
    }>('GET', `/splitters/${encodeURIComponent(splitterId)}/ports/${port}/path`),
  assetOptions: (kind: AddableKind, tier: CableTier) => call<AssetOptions>('GET', `/assets/options?kind=${kind}&tier=${tier}`),
  createNode: (body: { kind: AddableKind; name: string; location: LngLat; status: 'planned' | 'active'; locality?: string; address?: string; splitterRatio?: number }) =>
    call<{ node: PlantNode }>('POST', '/nodes', body),
  moveNode: (id: string, location: LngLat, expectedRevision: number) => call<{ node: PlantNode }>('PATCH', `/nodes/${encodeURIComponent(id)}/location`, { location, expectedRevision }),
  deleteNode: (id: string, expectedRevision: number) => call<void>('DELETE', `/nodes/${encodeURIComponent(id)}?expectedRevision=${expectedRevision}`),
  streetRoute: (fromNodeId: string, toNodeId: string, tier: CableTier) =>
    call<{ via: LngLat[]; lengthM: number; straightM: number; detour: boolean }>('POST', '/routes/streets', { fromNodeId, toNodeId, tier }),
  createCable: (body: { name: string; tier: CableTier; fromNodeId: string; toNodeId: string; fiberCount: number; status: 'planned' | 'active'; via: LngLat[]; measuredLengthM?: number }) =>
    call<{ cable: { id: string; name: string } }>('POST', '/cables', body),
  deleteCable: (id: string) => call<void>('DELETE', `/cables/${encodeURIComponent(id)}`),
  importPlan: (filename: string, content: string, options: ImportOptionsIn) => call<ImportPlan>('POST', '/import/plan', { filename, content, options }),
  importApply: (filename: string, content: string, options: ImportOptionsIn) =>
    call<{ nodes: { id: string; name: string }[]; cables: { id: string; name: string }[]; failures: { source: string; message: string }[] }>('POST', '/import/apply', {
      filename,
      content,
      options,
    }),
  ackIncident: (id: string) => call<{ ack: { by: string; at: string } }>('POST', `/incidents/${encodeURIComponent(id)}/ack`, {}),
  demoAlarm: (body: { kind: 'cut'; cableId: string } | { kind: 'power'; nodeId: string } | { kind: 'clear' }) => call<{ ok: true }>('POST', '/demo/alarms', body),
  addConnection: (body: { nodeId: string; expectedRevision: number; kind: Connection['kind']; a: EndpointRef; b: EndpointRef; tray?: number; lossDb?: number }) =>
    call<{ connection: Connection; revision: number }>('POST', '/connections', body),
  removeConnection: (id: string, expectedRevision: number) =>
    call<{ revision: number }>('DELETE', `/connections/${encodeURIComponent(id)}?expectedRevision=${expectedRevision}`),
}
